// ============================================================
// MANIC MEETER — Deutsche Inhalte: Upgrades, Perks, Preise, Enden
// ============================================================

import {
  AchievementId, EndingType, PermanentUnlockId, PowerupId, StatUpgradeId, WeekendEventType,
} from "../../types";
import type {
  EndingContent, MeetingTypeContent, PermanentUnlockContent, PositiveEventContent, TextEntry,
  VendorContent,
} from "../contract";

export const STAT_UPGRADES: Record<StatUpgradeId, TextEntry> = {
  [StatUpgradeId.Gehalt]: { name: "💰 Gehaltsverhandlung", description: "Startgehalt +200€ pro Level" },
  [StatUpgradeId.MaxMuedigkeit]: { name: "☕ Koffein-Toleranz", description: "Max-Müdigkeit +10 pro Level" },
  [StatUpgradeId.Unauffaelligkeit]: { name: "🥷 Unauffälligkeit", description: "Ansprech-Chance −10% pro Level" },
  [StatUpgradeId.CriticalHit]: { name: "⚡ Critical Hit", description: "+10% Chance auf doppelte Müdigkeitsreduktion" },
  [StatUpgradeId.StandingDesk]: { name: "🪑 Standing Desk", description: "-1 Müdigkeit pro Runde (passiv)" },
  [StatUpgradeId.VPN]: { name: "🔒 VPN-Zugang", description: "+5% Chance, ein Meeting zu überspringen" },
  [StatUpgradeId.Ohrstoepsel]: { name: "🎵 Bluetooth-Ohrstöpsel", description: "Kollegen-Müdigkeit −1 pro Redeblock" },
  [StatUpgradeId.Kreditkarte]: { name: "💳 Firmen-Kreditkarte", description: "Gehaltsstrafen −10% pro Level" },
};

export const POWERUPS: Record<PowerupId, TextEntry> = {
  [PowerupId.Kaffee]: { name: "☕ Kaffee", description: "Sofort −25 Müdigkeit" },
  [PowerupId.HandyAkku]: { name: "📱 Handy-Akku", description: "Nächstes Prokrastinieren: 0% Erwischt-Chance" },
  [PowerupId.NoiseCancelling]: { name: "🎧 Noise-Cancelling", description: "Müdigkeit durch Kollegen −50% für 1 Meeting" },
  [PowerupId.FakeNotizen]: { name: "📋 Fake-Notizen", description: "Nächste Frage automatisch richtig" },
  [PowerupId.LaptopTarnung]: { name: "💻 Laptop-Tarnung", description: "Prokrastinieren sieht 3 Runden wie Arbeiten aus" },
  [PowerupId.PizzaBestellen]: { name: "🍕 Pizza bestellen", description: "Überspringe 1 Meeting komplett" },
  [PowerupId.MuteButton]: { name: "🔇 Mute-Button", description: "Immun gegen Ansprechen für 1 Meeting" },
  [PowerupId.RemoteJoker]: { name: "📡 Remote-Joker", description: "Meeting wird online — 40% weniger Ansprech-Chance" },
  [PowerupId.BullshitBingo]: { name: "📊 Bullshit-Bingo", description: "Buzzwords zählen — Bonus-Müdigkeitsreduktion am Meeting-Ende" },
  [PowerupId.PromptBibliothek]: { name: "📚 Prompt-Bibliothek", description: "Alle Minispiel-Timer +60% für 1 Meeting" },
  [PowerupId.KiZusammenfassung]: { name: "📝 KI-Zusammenfassung", description: "Die KI hat schon zusammengefasst: Meeting ist 2 Runden kürzer" },
  [PowerupId.KiAgent]: { name: "🤖 KI-Agent", description: "Arbeitet nachts durch: +150€, aber +10 Müdigkeit vom Gegenlesen" },
};

export const PERKS: Record<string, TextEntry> = {
  // Positiv
  guterSchlaf: { name: "Guter Schlaf", description: "20% weniger Müdigkeitsaufbau" },
  motivationsschub: { name: "Motivationsschub", description: "+50% Müdigkeitsabbau" },
  fluesterModus: { name: "Flüster-Modus", description: "20% geringere Ansprech-Chance" },
  sparsamkeit: { name: "Sparsamkeit", description: "Gehaltsstrafen -25%" },
  zuckerflash: { name: "Zuckerflash", description: "+15 Max-Müdigkeit" },
  networking: { name: "Networking-Skills", description: "+50€ pro überlebtem Tag" },
  flowState: { name: "Flow-State", description: "Minispiel-Timer +25%" },
  doppelterEspresso: { name: "Doppelter Espresso", description: "30% weniger Müdigkeitsaufbau" },
  chefImUrlaub: { name: "Chef im Urlaub", description: "40% geringere Ansprech-Chance" },
  fruehstuecksbuffet: { name: "Frühstücksbuffet", description: "+20 Max-Müdigkeit & Strafen -15%" },
  glueckssocken: { name: "Glückssocken", description: "Minispiel-Timer +40%" },
  parkplatzGefunden: { name: "Parkplatz gefunden!", description: "+75€ gute Laune Bonus" },
  kiCopilot: { name: "KI-Copilot", description: "Die KI denkt mit: 25% weniger Müdigkeitsaufbau" },
  promptGefunden: { name: "Der Prompt sitzt", description: "Endlich der richtige Prompt: Minispiel-Timer +30%" },
  agentLaeuft: { name: "Der Agent läuft", description: "Er arbeitet, du erholst dich: +40% Müdigkeitsabbau" },
  modellUpdate: { name: "Modell-Update", description: "Das neue Modell ist wirklich besser: +20 Max-Müdigkeit" },
  // Negativ
  langeGezockt: { name: "Lange gezockt", description: "+20 Müdigkeit am Tagesstart" },
  kater: { name: "Kater", description: "+25% Müdigkeitsaufbau" },
  schlechtGeschlafen: { name: "Schlecht geschlafen", description: "-30% Müdigkeitsabbau" },
  auffaellig: { name: "Auffällig gekleidet", description: "+25% Ansprech-Chance" },
  mieterhoehung: { name: "Mieterhöhung", description: "-30€ Gehalt pro Tag" },
  sodbrennen: { name: "Sodbrennen", description: "-10 Max-Müdigkeit" },
  ablenkung: { name: "Ablenkung", description: "Minispiel-Timer -25%" },
  montagsblues: { name: "Montagsblues", description: "Tagwechsel heilt nur halb" },
  stau: { name: "Stau gehabt", description: "+15 Müdigkeit & Strafen +20%" },
  tastaturDefekt: { name: "Tastatur defekt", description: "Minispiel-Timer -35%" },
  kantineGeschlossen: { name: "Kantine geschlossen", description: "-50% Müdigkeitsabbau & -15 Max-Müdigkeit" },
  druckerStau: { name: "Druckerstau", description: "+30% Ansprech-Chance" },
  windowsUpdate: { name: "Windows Update", description: "Tagwechsel heilt nur 30%" },
  kiAusfall: { name: "KI-Ausfall", description: "Du musst heute selbst denken: -40% Müdigkeitsabbau" },
  halluziniertesProtokoll: {
    name: "Halluziniertes Protokoll",
    description: "Die KI schreibt dir Zusagen ins Protokoll: +30% Ansprech-Chance",
  },
  kontextfensterVoll: {
    name: "Kontextfenster voll",
    description: "Die KI hat den Anfang vergessen, du erklärst neu: Minispiel-Timer -30%",
  },
  promptInjection: {
    name: "Prompt Injection",
    description: "Jemand hat deinen Namen in die Agenda geschmuggelt: +35% Ansprech-Chance",
  },
};

export const ACHIEVEMENTS: Record<AchievementId, TextEntry> = {
  [AchievementId.MeetingMarathon]: { name: "Meeting-Marathon", description: "Überlebe 10 Meetings an einem Tag" },
  [AchievementId.Unsichtbar]: { name: "Unsichtbar", description: "Überstehe ein Meeting ohne angesprochen zu werden" },
  [AchievementId.Schlafwandler]: { name: "Schlafwandler", description: "Schlafe 3x ein und überlebe trotzdem den Tag" },
  [AchievementId.BullshitBingo]: { name: "Bullshit-Bingo", description: "Höre 15+ Buzzwords in einem Meeting (mit Bingo-Karte)" },
  [AchievementId.Unantastbar]: { name: "Unantastbar", description: "Prokrastiniere 20x hintereinander ohne erwischt zu werden" },
  [AchievementId.MonologKoenig]: { name: "Monolog-König", description: "Halte 5 ungefragte Monologe an einem Tag" },
  [AchievementId.Speedrunner]: { name: "Speedrunner", description: "Beantworte alle Fragen in einem Meeting korrekt (min. 2)" },
  [AchievementId.Gehaltsverhandler]: { name: "Gehaltsverhandler", description: "Beende einen Tag mit mehr Gehalt als am Start" },
  [AchievementId.Fluesterer]: { name: "Flüsterer", description: "Sprich 3 Meetings nur wenn angesprochen" },
  [AchievementId.Legend]: { name: "Legend", description: "Überlebe 30 Tage" },
  [AchievementId.KoffeinJunkie]: { name: "Koffein-Junkie", description: "Benutze 10 Kaffee-Powerups insgesamt" },
  [AchievementId.TeflonBeschichtung]: { name: "Teflon-Beschichtung", description: "Überlebe 5 Vorstandsmeetings" },
  [AchievementId.Kartenhai]: { name: "Kartenhai", description: "Gewinne 5 Kartenziehungen in der Rüstzeit" },
  [AchievementId.MinigameMeister]: { name: "Minigame-Meister", description: "Gewinne 20 Minigames insgesamt" },
  [AchievementId.Ueberlebenskuenstler]: { name: "Überlebenskünstler", description: "Überlebe einen Tag mit Gehalt unter 1200€" },
};

export const ENDINGS: Record<EndingType, EndingContent> = {
  [EndingType.Fired]: {
    title: "GEKÜNDIGT",
    description: "Dein Gehalt ist unter den Mindestlohn gefallen. Die HR hat dich zum Gespräch gebeten... ein letztes Mal.",
  },
  [EndingType.Burnout]: {
    title: "BURNOUT",
    description: "Du bist an einem Tag 5 Mal eingeschlafen. Der Betriebsarzt hat dich krankgeschrieben. Für immer.",
  },
  [EndingType.Retired]: {
    title: "FRÜHPENSIONIERT",
    description: "Du hast genug Meeting-Stunden gesammelt und gehst in den wohlverdienten Vorruhestand!",
  },
  [EndingType.Promoted]: {
    title: "BEFÖRDERT",
    description: "Unfassbar! Du wurdest zum Senior befördert. Jetzt organisierst DU die Meetings. Der Kreis schließt sich.",
  },
  [EndingType.Whistleblower]: {
    title: "WHISTLEBLOWER",
    description: "Mit 20 ausgefüllten Bullshit-Bingo-Karten hast du genug Beweise gesammelt. Du hast alles an die Presse geleakt!",
  },
};

export const PERMANENT_UNLOCKS: Record<PermanentUnlockId, PermanentUnlockContent> = {
  [PermanentUnlockId.Firmenwagen]: {
    name: "Firmenwagen",
    description: "Die Firma stellt dir einen Dienstwagen.",
    effect: "+100€ Startgehalt",
  },
  [PermanentUnlockId.BueroHund]: {
    name: "Büro-Hund",
    description: "Ein Hund im Büro senkt den Stresslevel aller.",
    effect: "-5% Müdigkeitsaufbau permanent",
  },
  [PermanentUnlockId.KantineFlatrate]: {
    name: "Kantine-Flatrate",
    description: "All-you-can-eat in der Firmenkantine.",
    effect: "+20 Max-Müdigkeit permanent",
  },
  [PermanentUnlockId.GoldeneBadge]: {
    name: "Goldene Mitarbeiter-Badge",
    description: "Deine Badge glänzt. Kollegen respektieren dich.",
    effect: "-8% Ansprech-Chance permanent",
  },
  [PermanentUnlockId.Homeoffice]: {
    name: "Homeoffice-Vertrag",
    description: "15% Chance, dass ein Meeting automatisch online ist.",
    effect: "15% Chance auf Online-Meeting",
  },
  [PermanentUnlockId.Dienstwohnung]: {
    name: "Dienstwohnung",
    description: "Wohnung direkt neben dem Büro. Besser schlafen.",
    effect: "+15% Tagerholung",
  },
};

export const VENDORS: Record<string, VendorContent> = {
  baeckerauto: {
    name: "Bäckerauto",
    intro: "Das Bäckerauto steht auf dem Parkplatz! Der Fahrer hat auch... andere Sachen im Angebot.",
    itemLabels: {
      [PowerupId.Kaffee]: "☕ Kaffee (−25 Müdigkeit)",
      [PowerupId.NoiseCancelling]: "🎧 Noise-Cancelling (−50% Kollegen-Müdigkeit)",
      [PowerupId.MuteButton]: "🔇 Mute-Button (Immun gegen Ansprechen)",
      [PowerupId.FakeNotizen]: "📋 Fake-Notizen (Auto-Antwort)",
    },
  },
  hardwarelieferant: {
    name: "Sammelbestellung Hardwarelieferant",
    intro: "Die IT hat eine Sammelbestellung beim Hardwarelieferant aufgegeben. Du kannst was mitbestellen...",
    itemLabels: {
      [PowerupId.RemoteJoker]: "📡 Remote-Joker (Meeting online)",
      [PowerupId.LaptopTarnung]: "💻 Laptop-Tarnung (3 Runden unsichtbar)",
      [PowerupId.HandyAkku]: "📱 Handy-Akku (1x sicher prokrastinieren)",
      [PowerupId.PizzaBestellen]: "🍕 Pizza bestellen (Meeting überspringen)",
    },
  },
  kaffeedealer: {
    name: "Kaffee-Dealer aus der Buchhaltung",
    intro: "Torsten aus der Buchhaltung dealt wieder mit Premium-Kaffee hinterm Kopierer.",
    itemLabels: {
      [PowerupId.Kaffee]: "☕ Kaffee (−25 Müdigkeit)",
      [PowerupId.BullshitBingo]: "📊 Bullshit-Bingo-Karte",
      [PowerupId.NoiseCancelling]: "🎧 Noise-Cancelling",
    },
  },
};

export const BINGO_GOALS: Record<string, string> = {
  survive: "Meeting überleben",
  noSleep: "Nicht eingeschlafen",
  noCatch: "Nicht erwischt",
  minigame: "Minigame gewonnen",
  monologue: "Monolog gehalten",
  invisible: "Unsichtbar geblieben",
  question: "Frage korrekt",
  proc3x: "3x prokrastiniert",
  dirChange: "Richtungswechsel erlebt",
};

export const WEEKEND_EVENTS: Record<WeekendEventType, MeetingTypeContent> = {
  [WeekendEventType.TeamBuilding]: {
    name: "Team-Building",
    description: "Teambuilding-Tag! Vertrauensübungen, Seilgarten und verpflichtender Spaß.",
  },
  [WeekendEventType.Betriebsfeier]: {
    name: "Betriebsfeier",
    description: "Betriebsfeier! Warm-Buffet, schlechte Musik und der Chef singt Karaoke.",
  },
  [WeekendEventType.Sommerfest]: {
    name: "Sommerfest",
    description: "Sommerfest auf dem Firmenparkplatz. Bratwurst und Networking.",
  },
  [WeekendEventType.Weihnachtsfeier]: {
    name: "Weihnachtsfeier",
    description: "Weihnachtsfeier! Wichteln, Glühwein und emotionale Reden vom Chef.",
  },
  [WeekendEventType.HackDay]: {
    name: "Hack Day",
    description: "Hack Day! 8 Stunden coden... in Meetings über das Coden.",
  },
};

export const POSITIVE_EVENTS: Record<string, PositiveEventContent> = {
  freeCoffee: { title: "☕ Gratis-Kaffee!", description: "Jemand hat Kaffee in die Küche gestellt." },
  donuts: { title: "🍩 Donuts im Pausenraum!", description: "Der Chef spendiert. Zucker-Boost!" },
  fireAlarm: { title: "🔥 Feueralarm!", description: "Alle raus! Das nächste Meeting fällt aus." },
  overtimeBonus: { title: "💸 Überstunden-Bonus!", description: "HR hat versehentlich einen Bonus überwiesen." },
  meetingCancelled: { title: "🏠 Meeting abgesagt!", description: "Der Organisator ist krank — Meeting fällt aus!" },
  mindfulness: { title: "🧘 Achtsamkeits-Pause!", description: "HR verordnet 5 Minuten Meditation." },
  birthdayCake: { title: "🎂 Geburtstagskuchen!", description: "Jemand hat Geburtstag. Kuchen für alle!" },
  praise: { title: "📧 Lob vom Chef!", description: "Eine seltene E-Mail: 'Gute Arbeit!' Gehaltsplus!" },
};
